import { jwtDecode } from "@Front/Utils/jwtDecodeWrapper";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { ThirdPartyStore } from "@Front/Stores/ThirdPartyStore";
import { UserStore } from "@Front/Stores/UserStore";
import {
	getLeCoffreRefreshTokenCookieForContext,
	type LeCoffreAuthContext,
} from "@Front/Utils/lecoffreSessionCookieHelpers";

type JwtWithJti = { jti?: string };

export function extractAccessTokenJti(token: string | null | undefined): string | null {
	if (!token) {
		return null;
	}
	try {
		const payload = jwtDecode<JwtWithJti>(token);
		return typeof payload?.jti === "string" && payload.jti.length > 0 ? payload.jti : null;
	} catch {
		return null;
	}
}

/**
 * When store and cookie disagree (multi-tab / post-relogin), prefer the cookie and rehydrate the store.
 */
export function resolveConsistentAccessToken(
	context: LeCoffreAuthContext,
	storeToken: string | null,
	cookieToken: string | null,
): string | null {
	if (storeToken && cookieToken) {
		const storeJti = extractAccessTokenJti(storeToken);
		const cookieJti = extractAccessTokenJti(cookieToken);
		if (storeJti && cookieJti && storeJti !== cookieJti) {
			LoggerService.warn("⚠️ [SessionToken] Store/cookie jti mismatch — preferring cookie", {
				context,
				storeJti,
				cookieJti,
			});
			void rehydrateStoreFromCookieTokens(context, cookieToken);
			return cookieToken;
		}
		return storeToken;
	}
	return storeToken ?? cookieToken;
}

async function rehydrateStoreFromCookieTokens(context: LeCoffreAuthContext, accessToken: string): Promise<void> {
	const refreshToken = getLeCoffreRefreshTokenCookieForContext(context);
	if (!refreshToken) {
		return;
	}
	try {
		if (context === "notary") {
			await UserStore.instance.connect(accessToken, refreshToken);
			return;
		}
		if (context === "customer") {
			await CustomerStore.instance.connect(accessToken, refreshToken);
			return;
		}
		await ThirdPartyStore.instance.connect(accessToken, refreshToken);
	} catch (error) {
		LoggerService.warn("⚠️ [SessionToken] Failed to rehydrate store from cookie", { context, error });
	}
}
